<template>
  <div
    class="heatmap-chart"
    :style="{
      width: config.chartWidth + 'px',
      height: config.chartHeight + 'px',
    }">
    <div class="heatmap-chart-wrapper">
      <div
        class="heatmap-chart-title"
        :style="{
          width: '100%',
          textAlign: style.titleStyle.align,
          fontSize: style.titleStyle.fontSize + 'px',
          color: style.titleStyle.color,
        }">
        {{ config.chartTitle }}
      </div>
      <img
        v-if="borderImgUrl"
        class="heatmap-chart-border"
        :src="borderImgUrl"
        alt=""
        :style="{ display: 'inline-block', width: '100%', height: '100%' }" />
    </div>
    <div ref="heatMap" class="heatmap-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { imgURLtoBase64 } from '@/utils/common'
import { heatMapOption } from './options'
import regionLngLat from '@/utils/region_lng_lat'
import ChartMixin from './mixin'
import { COLOR_LIST } from '@/utils/defaultColorData'

export default {
  mixins: [ChartMixin],
  data () {
    return {
      regionLngLatMap: {},
      amapStyle: [
        '',
      ],
    }
  },
  computed: {
    style () {
      return this.config.chartSet
    },
    chartStyle () {
      return this.config.chartSet.chartStyle
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
    renderData () {
      if (this.chartData?.length && Array.isArray(this.chartData)) {
        const list = []
        // 读取 第1列 & 第2列 的表头key
        const colKeyList = Object.keys(this.chartData[0]).slice(0, 2)
        // 表格数据 (去除第1行(表头))
        const tableData = this.chartData.slice(1)
        tableData.forEach(bar => {
          if ((bar[colKeyList[0]] !== '' || bar[colKeyList[0]] !== undefined) && (bar[colKeyList[1]] !== '' || bar[colKeyList[1]] !== undefined)) {
            list.push({
              name: bar[colKeyList[0]], // 第1列数据(维度)
              value: bar[colKeyList[1]], // 第2列数据(值)
            })
          }
        })
        return list.length ? list.sort((a, b) => b.value - a.value) : []
      } else {
        return []
      }
    },
    // 获取列名
    categoryName () {
      const category = this.chartData[0].col_1
      return category
    },

  },
  watch: {
    // 全局样式监听, 主题颜色修改
    'globalConfig.chartColors': {
      handler () {
        this.init()
      },
      deep: true,
    },
    chartData: {
      handler: function (val) {
        this.init()
      },
      deep: true,
    },
    // 配置项变更
    config: {
      handler: function () {
        this.init()
      },
      deep: true,
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },

  },
  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }

    if (!this.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }

    regionLngLat.region.forEach(bar => {
      if (!this.regionLngLatMap[bar.name]) {
        this.regionLngLatMap[bar.name] = bar
      }
    })
  },
  mounted () {
    this.charts = echarts.init(this.$refs.heatMap)
    this.init()
  },
  methods: {

    async init () {
      this.option = _.merge(_.cloneDeep(heatMapOption), {
        amap: {
          center: [this.chartStyle.lng, this.chartStyle.lat],
          zoom: this.chartStyle.zoom,
        },
        series: [
          {
            data: this.convertData(this.renderData),
            label: {
              show: this.chartStyle.isLabel,
              fontSize: this.chartStyle.fontSize,
            },
            itemStyle: {
              color: COLOR_LIST.filter(item => { return item.label === this.globalConfig.chartColors })[0].value[0],
            },
          },
          // 高亮
          {
            data: this.convertData(this.renderData).slice(0, this.chartStyle.highlightCount),
            label: {
              fontSize: this.chartStyle.fontSize,
            },
            itemStyle: {
              color: COLOR_LIST.filter(item => { return item.label === this.globalConfig.chartColors })[0].value[1],
            },
          },
        ],
      })

      this.charts.setOption(this.option)

      const amap = this.charts.getModel().getComponent('amap').getAMap()
      // 设置地图样式
      amap.setMapStyle(`amap://styles/${this.chartStyle.mapStyle}`)

      // resize
      this.$nextTick(() => {
        this.charts.resize()
      })
    },

    // 提示轮播
    tooltipsActive () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      const dataLength = this.renderData.length
      // 用定时器控制高亮
      this.timer = setInterval(() => {
        // 清除之前的高亮
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.index === dataLength - 1) {
          this.index = 0
        } else {
          this.index++
        }

        // 当前下标高亮
        this.charts.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.index,
        })

        this.charts.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.index,
        })
      }, this.config.chartSet.chartStyle.carouselDelay * 1000)
    },

    // 将数据转成echarts所需格式
    convertData (data) {
      const list = []
      data.forEach(bar => {
        const regionInfo = this.regionLngLatMap[bar.name]
        if (regionInfo) {
          list.push({
            name: bar.name, // 第1列数据(维度)
            value: [regionInfo.lng, regionInfo.lat, bar.value], // 第2列数据(值)
          })
        }
      })
      return list
    },
    // 鼠标移入移出
    mouseEvents () {
      // 鼠标划入
      this.charts.on('mouseover', () => {
        // 停止定时器，清除之前的高亮
        clearInterval(this.timer)
        this.timer = ''
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
      })
      // 鼠标划出重新定时器开始
      this.charts.on('mouseout', () => {
        this.tooltipsActive()
      })
    },
  },
}
</script>

<style lang='scss' scoped>
.heatmap-chart {
  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width: 100%;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;
  }
  &-title {
    padding: 7px 30px;
  }
  .heatmap-chart-container {
    flex: 1;
    position: relative;
  }
}
</style>
